import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  createStatisticsTrainingChangeRequests,
  getStatisticsTrainingRequestContext,
} from '../api/training';

function formatHours(value) {
  const totalMinutes = Math.round(Number(value || 0) * 60);
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')} h`;
}

function StatisticsLogbookDeleteRequestDialog({ entryId, open, scope, onClose, onSubmitted }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [context, setContext] = useState(null);
  const [targets, setTargets] = useState([]);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadContext = async ({ preserveError = false } = {}) => {
    setLoading(true);
    if (!preserveError) setError('');
    try {
      const { data } = await getStatisticsTrainingRequestContext(entryId);
      setContext(data);
      setTargets((scope === 'group' ? data.targets || [] : []).map((target) => ({ ...target, selected: false })));
    } catch (loadError) {
      setContext(null);
      setTargets([]);
      setError(loadError.response?.data?.error || 'Löschinformationen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !entryId) return;
    setReason('');
    setConfirmed(false);
    loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, open, scope]);

  const selectedTargets = scope === 'group'
    ? targets.filter((target) => target.selected)
    : context?.entry && context.targets?.find((target) => Number(target.entryId) === Number(context.entry.id))?.canSelect
      ? [{ entryId: context.entry.id }]
      : [];
  const singleRequestBlocked = scope === 'single' && context && !selectedTargets.length;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await createStatisticsTrainingChangeRequests(entryId, {
        action: 'delete',
        scope,
        reason: reason.trim(),
        confirmDeleteRequest: confirmed,
        targets: selectedTargets.map((target) => ({
          targetKind: target.targetKind,
          entryId: target.entryId,
          requestId: target.requestId,
          requestRevision: target.requestRevision,
        })),
      });
      onSubmitted(selectedTargets.length);
    } catch (submitError) {
      setError(submitError.response?.data?.error || 'Löschanfrage konnte nicht erstellt werden.');
      if (submitError.response?.status === 409) await loadContext({ preserveError: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{scope === 'group' ? 'Gruppenlöschung beantragen' : 'Löschung beantragen'}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 180 }}><CircularProgress /></Box>}
        {!loading && !context && error && <Alert severity="error">{error}</Alert>}
        {!loading && context && (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="warning">
              {scope === 'group'
                ? 'Für bestätigte Einträge wird eine Löschanfrage erstellt. Noch offene Eintragsanfragen werden direkt zurückgezogen.'
                : `Für den Logbucheintrag von ${context.owner?.fullName || 'der ausgewählten Person'} wird eine Löschanfrage erstellt.`}
            </Alert>
            {singleRequestBlocked && (
              <Alert severity="info">Für diesen Eintrag besteht bereits eine offene Änderungs- oder Löschanfrage.</Alert>
            )}

            {scope === 'single' ? (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{context.owner?.fullName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {`${context.entry?.date || '-'} · ${context.entry?.entryType || '-'} · ${formatHours(context.entry?.hours)}`}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {targets.map((target, index) => (
                  <Box key={`${target.targetKind}:${target.entryId || target.requestId}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={target.selected}
                            disabled={!target.canSelect}
                            onChange={(event) => setTargets((current) => current.map((item, targetIndex) => (
                              targetIndex === index ? { ...item, selected: event.target.checked } : item
                            )))}
                          />
                        )}
                        label={target.fullName}
                      />
                      {!target.canSelect && <Chip size="small" variant="outlined" color="warning" label="Anfrage bereits offen" />}
                      {target.targetKind === 'pending-create' && <Chip size="small" variant="outlined" color="info" label="Noch nicht genehmigt" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {`${formatHours(target.original?.hours)} · ${target.original?.timeFrom || '--:--'} bis ${target.original?.timeTo || '--:--'}`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            <TextField required fullWidth multiline minRows={2} label="Begründung" value={reason} onChange={(event) => setReason(event.target.value)} />
            <FormControlLabel
              control={<Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />}
              label={`Ich bestätige, dass ich ${scope === 'group' ? 'für die ausgewählten Einträge' : 'für diesen Eintrag'} Löschanfragen stellen möchte.`}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Abbrechen</Button>
        <Button
          color="error"
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineIcon />}
          onClick={handleSubmit}
          disabled={loading || submitting || !context || !selectedTargets.length || !reason.trim() || !confirmed}
        >
          {scope === 'group'
            ? `${selectedTargets.length} Gruppeneintrag${selectedTargets.length === 1 ? '' : 'e'} verarbeiten`
            : 'Löschanfrage senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StatisticsLogbookDeleteRequestDialog;